import ROUTES from "config/routing";
import { ADM_NAVIGATION } from "./constants";
import { CampaignInfoFormData } from "./schemas/campaignInformation";
import { ADMCampaignData, CampaignAudience, Audience } from "./types";
import { apiClient } from "module/api";

export const validateUserAccess = (
  history: { push: (path: string) => void },
  user: { clientId: number },
  clientId: number
) => {
  if (user.clientId != clientId) {
    console.log({ ACCESS_DENIED: "Access denied" });
    return history.push(ROUTES.ACCESS_DENIED);
  }
};

export const isAheadByBusinessDays = (date: Date, businessDays: number) => {
  const dateToCheck = getNextDayByBusinessDays(new Date(), businessDays);

  const dateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dateToCheckLocal = new Date(dateToCheck.getFullYear(), dateToCheck.getMonth(), dateToCheck.getDate());

  return dateLocal >= dateToCheckLocal;
};
export const getNextDayByBusinessDays = (date: Date, businessDays: number): Date => {
  const result = new Date(date);
  let daysAdded = 0;

  while (daysAdded < businessDays) {
    result.setDate(result.getDate() + 1);

    const isWeekend = !isBusinessDay(result);

    if (!isWeekend) {
      daysAdded++;
    }
  }

  return result;
};
const isBusinessDay = (date: Date): boolean => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

export const getIndexOfCurrentStep = (lastActiveStep: string | null) => {
  const previousStep = ADM_NAVIGATION.find((item) => item.step === lastActiveStep);

  if (!previousStep) {
    return 0;
  }

  return Math.min(previousStep.index, ADM_NAVIGATION.length - 1);
};

export const parseTimestampStringDate = (timestamp: string | number | Date | undefined): Date | null => {
  if (!timestamp) {
    return null;
  }

  // If it's already a Date, validate
  if (timestamp instanceof Date) {
    return isNaN(timestamp.getTime()) ? null : timestamp;
  }

  // If it's a number (ms epoch)
  if (typeof timestamp === "number") {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
  }

  // If it's a string: handle digits-only (epoch) and ISO-like formats
  if (typeof timestamp === "string") {
    const trimmed = timestamp.trim();
    if (/^\d+$/.test(trimmed)) {
      const d = new Date(parseInt(trimmed, 10));
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
};

export const campaignHasUpdatedFields = (
  currentCampaignData: ADMCampaignData,
  newCampaignData: CampaignInfoFormData
) => {
  const startDate = parseTimestampStringDate(currentCampaignData.startDate) || new Date();
  return (
    currentCampaignData.campaignName !== newCampaignData.campaignName ||
    startDate?.toISOString().split("T")[0] !== newCampaignData.campaignStartDate.toISOString().split("T")[0] ||
    currentCampaignData.flyerType !== newCampaignData.format ||
    currentCampaignData.updateAddressOnMove !== (newCampaignData.updateAddressOnMove === "yes")
  );
};

export const isCampaignInfoFormFullyFilled = (campaignData: CampaignInfoFormData): boolean => {
  return !!(
    campaignData.campaignName &&
    campaignData.campaignName.length > 0 &&
    campaignData.campaignStartDate &&
    campaignData.format &&
    campaignData.format.length > 0
  );
};

export const convertNumberRounded = ({ number = 0, currency = "", fixed = 2, isUSClient = false }) => {
  const multiplier = Number(addTrailingZeros(1, fixed + 1));
  const fixedNumber = String(Math.round((Number(number) || 0) * multiplier) / multiplier);
  const [num, decimal] = fixedNumber.split(".");
  const formattedNumber = Number(num).toLocaleString();
  return `${currency}${isUSClient ? formattedNumber : formattedNumber.replace(/,/g, " ")}.${addTrailingZeros(
    Number(decimal || 0),
    fixed
  )}`;
};

const addTrailingZeros = (num: number, totalLength: number) => {
  return String(num).padEnd(totalLength, "0");
};

export const validateCampaignRecipients = async (campaignId: string | null, clientId: string | null) => {
  if (!campaignId || !clientId) {
    return { isValid: false, totalRecipients: 0 };
  }
  try {
    const campaignAudiencesResponse = await apiClient.getCampaignAudiences(campaignId);
    if (campaignAudiencesResponse.status !== 200) {
      return { isValid: false, totalRecipients: 0 };
    }
    const campaignAudiences = await campaignAudiencesResponse.json();

    if (!campaignAudiences || campaignAudiences.length === 0) {
      return { isValid: false, totalRecipients: 0 };
    }

    const audienceIds = campaignAudiences.map((campaignAudience: CampaignAudience) => campaignAudience.audience_id);

    const selectedAudiences = await apiClient.getAudiencesByIds(audienceIds);

    const activeAudiences = selectedAudiences.filter((audience) => !audience.deletedAt);

    const totalRecipients = activeAudiences.reduce((acc, audience) => acc + audience.metadata.rowsAmount, 0);
    return { isValid: totalRecipients > 999, totalRecipients };
  } catch (error) {
    console.error("Error validating campaign recipients", error);
    return { isValid: false, totalRecipients: 0 };
  }
};
